import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import {Observable, catchError, map, of, switchMap, tap, BehaviorSubject} from 'rxjs';
import { environment } from '../../../environments/environment';
import { AcaoRequerida, LoginResponse, UsuarioAuth } from '../models/auth.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  private usuarioSubject = new BehaviorSubject<UsuarioAuth | null>(null);
  private propriedadeAtualSubject = new BehaviorSubject<number | null>(null);

  readonly acaoRequerida = signal<AcaoRequerida>(null);

  usuario$ = this.usuarioSubject.asObservable();
  propriedadeAtual$ = this.propriedadeAtualSubject.asObservable();
  isLogado$ = this.usuario$.pipe(map((u) => u !== null));

    get usuario(): UsuarioAuth | null {
        return this.usuarioSubject.value;
    }
    get propriedadeAtual(): number | null {
        return this.propriedadeAtualSubject.value;
    }

    setPropriedadeAtual(id: number | null): void {
        this.propriedadeAtualSubject.next(id);
    }

    readonly usuarioSig = toSignal(this.usuario$, { initialValue: null });

    temPermissao(...chaves: string[]): boolean {
        const permissoes = this.usuarioSig()?.permissoes;

        if (!permissoes) return false;

        return chaves.every((chave) => permissoes.includes(chave));
    }

    verificarSessao(): Observable<boolean> {
    return this.http
      .get<LoginResponse>(`${this.apiUrl}/api/usuario`)
      .pipe(
        tap((res) => {
          this.usuarioSubject.next(res.usuario);
          this.propriedadeAtualSubject.next(res.propriedadeAtual);
          this.acaoRequerida.set(res.acaoRequerida ?? null);
        }),
        map(() => true),
        catchError(() => {
          this.limparSessao();
          return of(false);
        }),
      );
  }

    login(email: string, senha: string): Observable<LoginResponse> {
        return this.http
            .get(`${this.apiUrl}/sanctum/csrf-cookie`, { responseType: 'text' })
            .pipe(
                switchMap(() =>
                    this.http.post<LoginResponse>(`${this.apiUrl}/api/login`, {
                        email,
                        senha,
                     }),
                ),
                tap((res) => {
                    this.usuarioSubject.next(res.usuario);
                    this.propriedadeAtualSubject.next(res.propriedadeAtual);
                    this.acaoRequerida.set(res.acaoRequerida ?? null);
                }),
            );
    }

  limparSessao(): void {
    this.usuarioSubject.next(null);
    this.propriedadeAtualSubject.next(null);
    this.acaoRequerida.set(null);
  }

  selecionarPropriedadeAtiva(id: number | null): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/api/sessao/propriedade-ativa`, { propriedade_id: id });
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/api/logout`, {}).pipe(
      tap(() => {
        this.limparSessao();
        // Derruba de uma vez todo estado em memória. Com hash routing, trocar
        // só o hash não recarrega a página: daí o reload() explícito.
        window.location.hash = '#/login';
        window.location.reload();
      }),
    );
  }

  esqueciSenha(email: string): Observable<{ mensagem: string }> {
    return this.http
      .get(`${this.apiUrl}/sanctum/csrf-cookie`, { responseType: 'text' })
      .pipe(
        switchMap(() =>
          this.http.post<{ mensagem: string }>(`${this.apiUrl}/api/solicitar-reset-senha`, { email }),
        ),
      );
  }

  alterarSenha(
    senhaAtual: string,
    senha: string,
    senhaConfirmacao: string,
  ): Observable<{ mensagem: string }> {
    return this.http.patch<{ mensagem: string }>(`${this.apiUrl}/api/usuario/senha`, {
      senha_atual: senhaAtual,
      senha,
      senha_confirmation: senhaConfirmacao,
    });
  }

  redefinirSenha(
    email: string,
    token: string,
    senha: string,
    senhaConfirmacao: string,
  ): Observable<{ mensagem: string }> {
    return this.http
      .get(`${this.apiUrl}/sanctum/csrf-cookie`, { responseType: 'text' })
      .pipe(
        switchMap(() =>
          this.http.post<{ mensagem: string }>(`${this.apiUrl}/api/resetar-senha`, {
            email,
            token,
            senha,
            senha_confirmation: senhaConfirmacao,
          }),
        ),
      );
  }
}
